/* UI-DESIGN: one glass form, explicit case selection and unnumbered headings
   simplify hosting without changing the query or create-game payload. */
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { ArrowLeft, ArrowRight, Check } from "@gravity-ui/icons";
import Input from "../../components/input";
import { useGetMysteriesQuery, useCreateGameMutation } from "../../reducers/apiSlice";
import { useToast } from "../../components/ui/toast";
import convertErrorMessage from "../../utils/convertErrorMessage";
import type { Mystery } from "../../types/game";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import { toCaseView } from "../../components/lightbox/caseView";
import { caseArtFor } from "../../components/lightbox/caseArt";
import { initialTypeface } from "../../components/lightbox/typefaces";
import styles from "./CreateGame.module.scss";

const noop = () => undefined;

interface CreateGameForm {
  name: string;
  mystery: string;
  host_is_playing: boolean;
}

const CreateGame = () => {
  const { data: mysteries = [], isLoading: mysteriesLoading, isError: mysteriesError } = useGetMysteriesQuery(undefined);
  const [createGame, { isLoading: isCreating }] = useCreateGameMutation();
  const { triggerToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedMystery = searchParams.get("mystery") ?? "";
  const caseList = useRef<HTMLDivElement>(null);
  const [visibleCase, setVisibleCase] = useState(0);
  // UI-DESIGN: browsing never changes the chosen mystery; selection stays explicit.
  const browseCase = (index: number) => {
    const list = caseList.current;
    const card = list?.children[index] as HTMLElement | undefined;
    if (list && card) list.scrollTo({ left: card.offsetLeft - (list.children[0] as HTMLElement).offsetLeft, behavior: "instant" });
  };
  const appliedRequest = useRef<string | null>(null);

  const form = useForm<CreateGameForm>({
    defaultValues: {
      name: "",
      mystery: "",
      host_is_playing: true,
    },
  });
  /* UI-DESIGN: watched so the selected card can show its gold ring and the
     submit area can confirm the chosen case by name */
  const selectedMystery = form.watch("mystery");
  const { getFieldState, setValue } = form;
  /* UI-DESIGN: a case chosen on Home arrives selected after live data
     loads. Never trust an arbitrary URL ID or replace a user's choice
     when the query refreshes. Auth and the create mutation are unchanged. */
  useEffect(() => {
    if (mysteriesLoading || mysteriesError || appliedRequest.current === requestedMystery) return;
    appliedRequest.current = requestedMystery;
    if (
      requestedMystery &&
      mysteries.some((m) => m.objectId === requestedMystery) &&
      !getFieldState("mystery").isDirty
    ) {
      setValue("mystery", requestedMystery, { shouldValidate: true });
    }
  }, [requestedMystery, mysteries, mysteriesLoading, mysteriesError, getFieldState, setValue]);
  useEffect(() => {
    const index = mysteries.findIndex((m) => m.objectId === selectedMystery);
    if (index >= 0) browseCase(index);
  }, [selectedMystery, mysteries]);
  const selectedName = mysteries.find(
    (m: Mystery) => m.objectId === selectedMystery
  )?.name;

  const onSubmit = (data: CreateGameForm) => {
    createGame({
      name: data.name,
      mystery: data.mystery,
      host_is_playing: data.host_is_playing,
    })
      .unwrap()
      .then((res: { objectId?: string }) => {
        triggerToast({
          title: "Game Created",
          description: "Your party is ready — time to invite your suspects",
        });
        if (res?.objectId) {
          navigate(`/lobby?gameId=${res.objectId}`);
        }
      })
      .catch((err) => {
        triggerToast({
          title: "Error",
          description: convertErrorMessage(err),
        });
      });
  };

  return (
    <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={noop}>
      <main className={styles.page}>
        <GlassPanel className={styles.layout}>
          <header className={styles.intro}>
            <h1 className={styles.title}>Create your game</h1>
            <p className={styles.sub}>Pick a case and name your event. You'll invite your guests next.</p>
          </header>

          <form className={styles.formCard} onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <div className={clsx(styles.step, styles.nameStep)}>
              <div className={styles.stepBody}>
                <Input
                  label="Name your party"
                  labelClassName={styles.stepTitle}
                  inputClassName={styles.field}
                  register={form.register("name", {
                    required: "Game name is required",
                  })}
                  placeholder="e.g. Sarah's Birthday Mystery"
                  error={form.formState.errors.name?.message}
                />
              </div>
            </div>

            {/* UI-DESIGN: headings carry the form order without decorative numbering. */}
            <fieldset className={styles.step} aria-labelledby="case-picker-title">
              <div className={styles.stepBody}>
                <h2 id="case-picker-title" className={styles.stepTitle}>Choose a case</h2>

                {mysteriesLoading ? (
                  /* UI-DESIGN: blank file rows hold the list's shape while loading */
                  <div className={styles.cases} aria-busy="true" aria-label="Loading cases">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={clsx(styles.caseOption, styles.skeleton)} />
                    ))}
                  </div>
                ) : mysteries.length === 0 ? (
                  <p className={styles.status}>No cases are available right now. Check back soon.</p>
                ) : (
                  <div className={styles.cases} ref={caseList} onScroll={(event) => {
                    const list = event.currentTarget;
                    const first = list.children[0] as HTMLElement;
                    const index = Array.from(list.children).findIndex((child) => Math.abs((child as HTMLElement).offsetLeft - first.offsetLeft - list.scrollLeft) < list.clientWidth / 2);
                    if (index >= 0) setVisibleCase(index);
                  }}>
                    {mysteries.map((m: Mystery, i: number) => {
                      const view = toCaseView(m, i);
                      const artwork = caseArtFor(m.name) ?? view.imageUrl;
                      const selected = selectedMystery === m.objectId;
                      return (
                        <label key={m.objectId} className={clsx(styles.caseOption, selected && styles.selected)}>
                          <input
                            type="radio"
                            value={m.objectId}
                            className={styles.radio}
                            aria-label={view.name}
                            {...form.register("mystery", {
                              required: "Please select a case",
                            })}
                          />
                          {artwork ? (
                            <img className={styles.thumb} src={artwork} alt="" loading="lazy" decoding="async" />
                          ) : (
                            <span className={clsx(styles.thumb, styles.thumbBlank)} aria-hidden />
                          )}
                          <span className={styles.caseText}>
                            <span className={styles.caseName}>{view.name}</span>
                            {view.premise ? <span className={styles.casePremise}>{view.premise}</span> : null}
                            {view.players || view.length ? (
                              <span className={styles.caseFacts}>
                                {[view.players, view.length].filter(Boolean).join(", ")}
                              </span>
                            ) : null}
                          </span>
                          <span className={styles.phoneChoice} aria-hidden>{selected ? "Selected" : "Choose this case"}</span>
                          <span className={styles.mark} aria-hidden>
                            {selected ? <Check width={16} height={16} /> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {!mysteriesLoading && mysteries.length > 1 && (
                  <div className={styles.browseControls}>
                    <LightboxButton type="button" tone="secondary" aria-label="Previous case" isDisabled={visibleCase === 0} onPress={() => browseCase(visibleCase - 1)} ><ArrowLeft /></LightboxButton>
                    <span aria-live="polite">{visibleCase + 1} / {mysteries.length}</span>
                    <LightboxButton type="button" tone="secondary" aria-label="Next case" isDisabled={visibleCase === mysteries.length - 1} onPress={() => browseCase(visibleCase + 1)} ><ArrowRight /></LightboxButton>
                  </div>
                )}
                {form.formState.errors.mystery?.message && (
                  <p className={styles.fieldError} role="alert">
                    {form.formState.errors.mystery.message}
                  </p>
                )}
              </div>
            </fieldset>

            {/* UI-DESIGN: hosts can opt out of playing a character while still
                running the game — defaults to playing */}
            <div className={styles.step}>
              <div className={styles.stepBody}>
                <p className={styles.stepTitle}>Are you playing?</p>
                <label className={styles.hostPlayingToggle}>
                  <input type="checkbox" {...form.register("host_is_playing")} />
                  <span>I&rsquo;m playing a character too (uncheck to only host)</span>
                </label>
              </div>
            </div>

            <div className={styles.submitRow}>
              {/* UI-DESIGN: quiet confirmation of the chosen case before the CTA */}
              <p className={styles.summary} aria-live="polite">
                {selectedName ? (
                  <>You're hosting <strong>{selectedName}</strong></>
                ) : (
                  "Pick a case to continue"
                )}
              </p>
              <LightboxButton type="submit" isDisabled={isCreating} trailing={isCreating ? undefined : <ArrowRight />}>
                {isCreating ? "Creating…" : "Create game"}
              </LightboxButton>
            </div>
          </form>
        </GlassPanel>
      </main>
    </LightboxSurface>
  );
};

export default CreateGame;
